import React from "react";

import { useFetchBiProfitabilityQuery } from "../../../api/analyticsApi";
import { Chip, Notice } from "../../catalogProductStudio/edit/StudioUi";
import { TrendChart } from "../AnalyticsCharts";
import { DataNotes, DataState, DataTable, KpiCard, Panel, ProductCell } from "../AnalyticsUi";
import { COST_STATUS_TONE, costBasisLabel, fill, formatMoney, formatNumber, formatPercent } from "../analyticsModel";

const statementLines = (statement) => {
  const cost = statement.cost || {};
  return [
    { key: "grossSales", value: statement.grossSales },
    { key: "discounts", value: statement.discounts },
    { key: "revenue", value: statement.revenue, strong: true },
    { key: "refunds", value: statement.refunds },
    { key: "netRevenue", value: statement.netRevenue, strong: true },
    { key: "uncovered", value: statement.netRevenue - (cost.coveredNetRevenue ?? 0), hideWhenZero: true },
    { key: "coveredNet", value: cost.coveredNetRevenue },
    { key: "cogsSold", value: cost.cogsSold },
    { key: "cogsReturned", value: cost.cogsReturned },
    { key: "grossProfit", value: cost.grossProfit, strong: true, total: true },
    { key: "grossMargin", value: cost.grossMargin, percent: true },
  ];
};

const Statement = ({ data, tt, lang }) => {
  const current = statementLines(data.statement.current);
  const previous = statementLines(data.statement.previous);
  return (
    <div className="an-table-wrap">
      <table className="an-table an-statement">
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col" className="is-end">{tt("currentPeriod")}</th>
            <th scope="col" className="is-end">{tt("previousPeriod")}</th>
          </tr>
        </thead>
        <tbody>
          {current.map((line, index) => {
            if (line.hideWhenZero && Math.abs(line.value || 0) < 0.005 && Math.abs(previous[index].value || 0) < 0.005) return null;
            const format = (value) => (line.percent ? formatPercent(value, lang) : formatMoney(value, lang));
            return (
              <tr key={line.key} className={line.total ? "is-total" : line.strong ? "is-strong" : undefined}>
                <th scope="row">{tt(`line_${line.key}`)}</th>
                <td className="is-end">{format(line.value)}</td>
                <td className="is-end an-muted">{format(previous[index].value)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CostStatus = ({ cost, tt }) =>
  cost ? <Chip tone={COST_STATUS_TONE[cost.status]}>{tt(`costStatus_${cost.status}`)}</Chip> : null;

const ProfitabilitySection = ({ range, tt, lang, costVisible }) => {
  const query = useFetchBiProfitabilityQuery(
    { dateFrom: range.dateFrom, dateTo: range.dateTo, granularity: range.granularity },
    { skip: !costVisible }
  );

  if (!costVisible) {
    return (
      <Notice tone="warning" icon="lock" title={tt("restrictedTitle")}>
        {tt("restrictedBody")}
      </Notice>
    );
  }

  const data = query.data;
  const productColumns = [
    {
      key: "product",
      label: tt("col_product"),
      className: "an-col-product",
      render: (row) => <ProductCell tt={tt} productId={row.productId} imageUrl={row.imageUrl} title={row.title} sub={row.categoryName || tt("uncategorised")} />,
    },
    { key: "units", label: tt("col_netUnits"), align: "end", render: (row) => formatNumber(row.netUnits, lang) },
    { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => formatMoney(row.netRevenue, lang) },
    { key: "profit", label: tt("col_profit"), align: "end", render: (row) => <strong>{formatMoney(row.cost?.grossProfit, lang)}</strong> },
    { key: "margin", label: tt("col_margin"), align: "end", render: (row) => formatPercent(row.cost?.grossMargin, lang) },
  ];

  return (
    <DataState query={query} tt={tt}>
      {data ? (
        <>
          <div className="an-kpis an-kpis--compact">
            <KpiCard tt={tt} lang={lang} label={tt("kpi_netRevenue")} compared={data.comparison.netRevenue} display={formatMoney(data.comparison.netRevenue.value, lang)} />
            <KpiCard tt={tt} lang={lang} invert label={tt("line_cogsSold").replace(/^[−+=]\s*/, "")} compared={data.comparison.netCogs} display={formatMoney(data.comparison.netCogs.value, lang)} badge={costBasisLabel(tt, data.statement.current.cost)} />
            <KpiCard tt={tt} lang={lang} tone="accent" label={tt("kpi_grossProfit")} help={tt("kpi_grossProfit_help")} compared={data.comparison.grossProfit} display={formatMoney(data.comparison.grossProfit.value, lang)} badge={costBasisLabel(tt, data.statement.current.cost)} />
            <KpiCard tt={tt} lang={lang} label={tt("kpi_grossMargin")} help={tt("kpi_grossMargin_help")} compared={data.comparison.grossMargin} display={formatPercent(data.comparison.grossMargin.value, lang)} badge={costBasisLabel(tt, data.statement.current.cost)} />
          </div>
          <DataNotes notes={data.notes} tt={tt} lang={lang} />

          <div className="an-grid an-grid--2">
            <Panel id="an-statement" title={tt("statementTitle")} hint={tt("statementHint")} actions={<CostStatus cost={data.statement.current.cost} tt={tt} />}>
              <Statement data={data} tt={tt} lang={lang} />
              <Notice tone="info" icon="info" title={tt("netProfitUnavailable")}>
                {tt("netProfitUnavailableHint")} {fill(tt("deliveryFeesHint"), { amount: formatMoney(data.deliveryFees, lang) })}
              </Notice>
            </Panel>
            <Panel id="an-profit-trend" title={tt("profitTrend")} hint={tt("revenueTrendHint")}>
              {data.statement.current.cost?.status === "unavailable" ? (
                <div className="an-empty">
                  <strong>{tt("unavailable")}</strong>
                  <span>{tt("note_cost_unavailable")}</span>
                </div>
              ) : (
                <TrendChart points={data.trend} field="grossProfit" granularity={data.granularity} label={tt("kpi_grossProfit")} lang={lang} tt={tt} />
              )}
            </Panel>
          </div>

          <div className="an-grid an-grid--2">
            <Panel id="an-profit-channel" title={tt("profitByChannel")}>
              <DataTable
                rows={data.channels}
                rowKey={(row) => row.channel}
                emptyText={tt("emptyTitle")}
                columns={[
                  { key: "channel", label: tt("col_channel"), render: (row) => tt(`channel_${row.channel}`) },
                  { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => formatMoney(row.netRevenue, lang) },
                  { key: "profit", label: tt("col_profit"), align: "end", render: (row) => <strong>{formatMoney(row.cost?.grossProfit, lang)}</strong> },
                  { key: "margin", label: tt("col_margin"), align: "end", render: (row) => formatPercent(row.cost?.grossMargin, lang) },
                  { key: "status", label: "", render: (row) => <CostStatus cost={row.cost} tt={tt} /> },
                ]}
              />
            </Panel>
            <Panel id="an-profit-category" title={tt("profitByCategory")}>
              <DataTable
                rows={data.categories}
                rowKey={(row) => row.key}
                emptyText={tt("emptyTitle")}
                columns={[
                  { key: "label", label: tt("category"), render: (row) => row.label || tt("uncategorised") },
                  { key: "net", label: tt("col_netRevenue"), align: "end", render: (row) => formatMoney(row.netRevenue, lang) },
                  { key: "profit", label: tt("col_profit"), align: "end", render: (row) => <strong>{formatMoney(row.cost?.grossProfit, lang)}</strong> },
                  { key: "margin", label: tt("col_margin"), align: "end", render: (row) => formatPercent(row.cost?.grossMargin, lang) },
                  { key: "status", label: "", render: (row) => <CostStatus cost={row.cost} tt={tt} /> },
                ]}
              />
            </Panel>
          </div>

          {data.productsMissingCost ? (
            <Notice tone="warning" title={fill(tt("missingCostCount"), { count: data.productsMissingCost })} />
          ) : null}
          <div className="an-grid an-grid--2">
            <Panel id="an-top-profit" title={tt("topProfit")}>
              <DataTable rows={data.topProducts} rowKey={(row) => row.productId} emptyText={tt("unavailable")} columns={productColumns} />
            </Panel>
            <Panel id="an-low-margin" title={tt("lowestMargin")}>
              <DataTable rows={data.lowestMargin} rowKey={(row) => row.productId} emptyText={tt("unavailable")} columns={productColumns} />
            </Panel>
          </div>
        </>
      ) : null}
    </DataState>
  );
};

export default ProfitabilitySection;
